import { useEffect, useState } from "react";
import { useIsVerified } from "src/components/email-verification";
import {
  MainCard,
  MainCardContent,
  MainCardTitle,
} from "src/components/main-card-type.tsx";
import { Button } from "src/components/ui/button";
import {
  checkCalculatorUsageLimitAndRedirect,
  loadSavedCalculatorData,
  saveCalculatorData,
} from "src/utils/functions";
import type { ResultsType } from "../calculadora-veiculo-eletrico_d";
import { 
  calcularComparativoVeiculos, 
  formatNumericInput, 
  sanitizeNumericValue 
} from "../utils/functions";

const calculatorKey = "veiculo-eletrico";

export const VeiculoEletricoInputCard = ({
  setResults,
}: {
  setResults: (value: ResultsType | null) => void;
}) => {
  const isVerified = useIsVerified();
  const [message, setMessage] = useState<string>("");

  const [distanciaAnual, setDistanciaAnual] = useState("");
  const [consumoCombustivel, setConsumoCombustivel] = useState("12");
  const [precoCombustivel, setPrecoCombustivel] = useState("6,20");
  const [consumoEletrico, setConsumoEletrico] = useState("9");
  const [precoEnergia, setPrecoEnergia] = useState("0,70");

  function clear() {
    setDistanciaAnual("");
    setConsumoCombustivel("12");
    setPrecoCombustivel("6,20");
    setConsumoEletrico("9");
    setPrecoEnergia("0,70");
    setResults(null);
    setMessage("");
  }

  function validarCampos() {
    const distancia = sanitizeNumericValue(distanciaAnual);
    const consumoC = sanitizeNumericValue(consumoCombustivel);
    const precoC = sanitizeNumericValue(precoCombustivel);
    const consumoE = sanitizeNumericValue(consumoEletrico);
    const precoE = sanitizeNumericValue(precoEnergia);

    if (!distanciaAnual || isNaN(distancia)) {
      setMessage("Informe a distância percorrida por ano.");
      return false;
    }

    if (distancia < 1000 || distancia > 100000) {
      setMessage("Distância anual deve estar entre 1.000 e 100.000 km.");
      return false;
    }

    if (!consumoCombustivel || isNaN(consumoC) || consumoC <= 0) {
      setMessage("Informe o consumo do veículo a combustível (km/litro).");
      return false;
    }

    if (consumoC < 3 || consumoC > 30) {
      setMessage("Consumo do veículo deve estar entre 3 e 30 km/l.");
      return false;
    }

    if (!precoCombustivel || isNaN(precoC) || precoC <= 0) {
      setMessage("Informe o preço do combustível.");
      return false;
    }

    if (precoC < 2 || precoC > 15) {
      setMessage("Preço do combustível deve estar entre R$ 2,00 e R$ 15,00.");
      return false;
    }

    if (!consumoEletrico || isNaN(consumoE) || consumoE <= 0) {
      setMessage("Informe o consumo do veículo elétrico (km/kWh).");
      return false;
    }

    if (consumoE < 3 || consumoE > 20) {
      setMessage("Consumo do veículo elétrico deve estar entre 3 e 20 km/kWh.");
      return false;
    }

    if (!precoEnergia || isNaN(precoE) || precoE <= 0) {
      setMessage("Informe o preço da energia elétrica.");
      return false;
    }

    if (precoE < 0.2 || precoE > 2.0) {
      setMessage("Preço da energia deve estar entre R$ 0,20 e R$ 2,00.");
      return false;
    }

    return true;
  }

  function calcular() {
    if (!validarCampos()) return;

    const distancia = sanitizeNumericValue(distanciaAnual);
    const consumoC = sanitizeNumericValue(consumoCombustivel);
    const precoC = sanitizeNumericValue(precoCombustivel);
    const consumoE = sanitizeNumericValue(consumoEletrico);
    const precoE = sanitizeNumericValue(precoEnergia);

    const results = calcularComparativoVeiculos(
      distancia,
      consumoC,
      precoC,
      consumoE,
      precoE
    );

    setResults(results);
    setMessage("");
  }

  function handleSave() {
    saveCalculatorData(calculatorKey, {
      distanciaAnual,
      consumoCombustivel,
      precoCombustivel,
      consumoEletrico,
      precoEnergia,
    });
  }

  useEffect(() => {
    const savedData = loadSavedCalculatorData(calculatorKey);
    if (!savedData) return;

    setDistanciaAnual((savedData.distanciaAnual as string) || "");
    setConsumoCombustivel((savedData.consumoCombustivel as string) || "12");
    setPrecoCombustivel((savedData.precoCombustivel as string) || "6,20");
    setConsumoEletrico((savedData.consumoEletrico as string) || "9");
    setPrecoEnergia((savedData.precoEnergia as string) || "0,70");
  }, []);

  return (
    <MainCard>
      <MainCardTitle className="mb-4">Calculadora: Veículo a Combustível vs Elétrico</MainCardTitle>
      <form
        onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
          e.preventDefault();
          handleSave();
          checkCalculatorUsageLimitAndRedirect(calcular);
        }}
      >
        <MainCardContent>
          <div className="col-span-2">
            <label
              className="block mb-2 font-medium text-sm"
              htmlFor="distancia-anual"
            >
              Distância percorrida por ano (km)
            </label>
            <div className="flex border">
              <input
                className="flex-1 p-2 min-w-[100px]"
                id="distancia-anual"
                placeholder="15.000"
                type="text"
                value={distanciaAnual}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const formatted = formatNumericInput(e.target.value);
                  setDistanciaAnual(formatted);
                }}
              />
            </div>
          </div>

          <div className="col-span-2 mt-3">
            <h3 className="font-semibold text-md mb-2 text-primary">
              Veículo a Combustível
            </h3>
          </div>

          <div className="sm:col-span-1 col-span-2">
            <label
              className="block mb-2 font-medium text-sm"
              htmlFor="consumo-combustivel"
            >
              Consumo médio (km/litro)
            </label>
            <div className="flex border">
              <input
                className="flex-1 p-2 min-w-[100px]"
                id="consumo-combustivel"
                placeholder="12,0"
                type="text"
                value={consumoCombustivel}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const formatted = formatNumericInput(e.target.value);
                  setConsumoCombustivel(formatted);
                }}
              />
            </div>
          </div>

          <div className="sm:col-span-1 col-span-2">
            <label
              className="block mb-2 font-medium text-sm"
              htmlFor="preco-combustivel"
            >
              Preço do combustível (R$/litro)
            </label>
            <div className="flex border">
              <span className="inline-flex items-center px-3 border-r text-sm bg-[#e9ecef]">
                R$
              </span>
              <input
                className="flex-1 p-2 min-w-[100px]"
                id="preco-combustivel"
                placeholder="6,20"
                type="text"
                value={precoCombustivel}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const formatted = formatNumericInput(e.target.value);
                  setPrecoCombustivel(formatted);
                }}
              />
            </div>
          </div>

          <div className="col-span-2 mt-3">
            <h3 className="font-semibold text-md mb-2 text-primary">
              Veículo Elétrico
            </h3>
          </div>

          <div className="sm:col-span-1 col-span-2">
            <label
              className="block mb-2 font-medium text-sm"
              htmlFor="consumo-eletrico"
            >
              Consumo médio (km/kWh)
            </label>
            <div className="flex border">
              <input
                className="flex-1 p-2 min-w-[100px]"
                id="consumo-eletrico"
                placeholder="9,0"
                type="text"
                value={consumoEletrico}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const formatted = formatNumericInput(e.target.value);
                  setConsumoEletrico(formatted);
                }}
              />
            </div>
          </div>

          <div className="sm:col-span-1 col-span-2">
            <label
              className="block mb-2 font-medium text-sm"
              htmlFor="preco-energia"
            >
              Preço da energia (R$/kWh)
            </label>
            <div className="flex border">
              <span className="inline-flex items-center px-3 border-r text-sm bg-[#e9ecef]">
                R$
              </span>
              <input
                className="flex-1 p-2 min-w-[100px]"
                id="preco-energia"
                placeholder="0,70"
                type="text"
                value={precoEnergia}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const formatted = formatNumericInput(e.target.value);
                  setPrecoEnergia(formatted);
                }}
              />
            </div>
          </div>
        </MainCardContent>

        <div className="flex justify-between">
          <Button
            disabled={!isVerified}
            type="submit"
            variant={"default"}
            className="sm:w-[150px]"
          >
            Calcular
          </Button>

          <Button onClick={clear} variant={"ghost"}>
            Limpar
          </Button>
        </div>
      </form>
      <p className="text-red-800 text-center mt-2 font-bold">{message}</p>
    </MainCard>
  );
};